import '@pgcustom/design-system';
import type { Resultado } from '../../core/resultado.js';
import type { IntegracoesApi, StatusMelhorEnvio } from './integracoes.api.js';
import type { PermissoesIntegracoes } from './integracoes.permissoes.js';

/** Destino pós-OAuth: a própria tela, na origem real de onde o admin está. */
function returnToAtual(): string {
  return `${window.location.origin}/#/integracoes/melhor-envio`;
}

function formatarDataHora(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}

const ROTULO_STATUS: Record<StatusMelhorEnvio['status'], { texto: string; tom: 'sucesso' | 'alerta' | 'erro' }> = {
  connected: { texto: 'Conectado', tom: 'sucesso' },
  expired: { texto: 'Autorização expirada', tom: 'alerta' },
  disconnected: { texto: 'Desconectado', tom: 'erro' },
};

/**
 * `<erp-melhor-envio-page>` — conexão/configuração do provedor de frete
 * Melhor Envio. O ERP NÃO reimplementa OAuth/token/cotação: a integração
 * inteira vive no site legado (pgcustom-art), já auditada e corrigida.
 * Esta tela é nativa do ERP mas fala com `/api/v1/integracoes/melhor-envio/*`,
 * um proxy fino para `/api/erp-bridge/melhor-envio/*` do site. "Conectar"
 * redireciona o navegador para o OAuth do Melhor Envio; o callback (no
 * site) salva o token no MESMO `company_settings` que a cotação lê e
 * devolve o navegador para cá.
 */
export class ErpMelhorEnvioPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _api: IntegracoesApi | null = null;
  private _permissoes: PermissoesIntegracoes = { ler: false, gerenciar: false };

  private resultado: Resultado<StatusMelhorEnvio> = { status: 'carregando' };
  private mensagem: { tom: 'sucesso' | 'erro' | 'info'; texto: string } | null = null;
  private testando = false;
  /**
   * `/status` é otimista: diz "conectado" sempre que existe um refresh_token salvo, mesmo
   * que o Melhor Envio já o tenha revogado (só uma chamada real revela isso). Guardamos
   * aqui o resultado da verificação automática feita no load para o badge nunca mostrar
   * "Conectado" quando na prática o frete está fora.
   */
  private liveness: { verificado: boolean; ok: boolean; erro: string | null } = {
    verificado: false,
    ok: false,
    erro: null,
  };

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set api(valor: IntegracoesApi) {
    this._api = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesIntegracoes) {
    this._permissoes = valor;
    if (this.isConnected) this.render();
  }

  connectedCallback(): void {
    this.lerFlashDaUrl();
    this.render();
    if (this._api) void this.carregar();
  }

  /** `?me_connected=1` / `?me_error=...` vêm do callback OAuth do site (antes do #). */
  private lerFlashDaUrl(): void {
    const params = new URLSearchParams(window.location.search);
    if (params.get('me_connected') === '1') {
      this.mensagem = { tom: 'sucesso', texto: 'Conta do Melhor Envio conectada com sucesso.' };
    } else if (params.get('me_error')) {
      this.mensagem = { tom: 'erro', texto: `Falha ao conectar: ${params.get('me_error')}` };
    }
    if (params.get('me_connected') || params.get('me_error')) {
      const limpa = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', limpa);
    }
  }

  private async carregar(): Promise<void> {
    if (!this._api) return;
    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.render();
      return;
    }
    this.resultado = { status: 'carregando' };
    this.liveness = { verificado: false, ok: false, erro: null };
    this.render();
    this.resultado = await this._api.statusMelhorEnvio();
    this.render();

    // Verificação de liveness: se o /status diz que há token, confirma com uma chamada real
    // (o /status não sabe se o Melhor Envio já revogou o refresh_token). Sem isto o operador
    // vê "Conectado" e assume que está tudo certo, mesmo com o frete fora do ar.
    if (this.resultado.status === 'ok' && (this.resultado.dado.status === 'connected' || this.resultado.dado.status === 'expired')) {
      try {
        const r = await this._api.testarMelhorEnvio();
        this.liveness = { verificado: true, ok: r.ok, erro: r.ok ? null : (r.error ?? 'conexão recusada') };
      } catch (erro) {
        this.liveness = {
          verificado: true,
          ok: false,
          erro: erro instanceof Error ? erro.message : 'não foi possível verificar a conexão',
        };
      }
      this.render();
    }
  }

  private async conectar(): Promise<void> {
    if (!this._api || !this._permissoes.gerenciar) return;
    try {
      const url = await this._api.urlAutorizacaoMelhorEnvio(returnToAtual());
      window.location.href = url;
    } catch {
      this.mensagem = { tom: 'erro', texto: 'não foi possível iniciar a autorização. tente novamente.' };
      this.render();
    }
  }

  private async testar(): Promise<void> {
    if (!this._api || this.testando) return;
    this.testando = true;
    this.mensagem = { tom: 'info', texto: 'Testando conexão com o Melhor Envio…' };
    this.render();
    try {
      const r = await this._api.testarMelhorEnvio();
      this.mensagem = r.ok
        ? { tom: 'sucesso', texto: `Conexão OK${r.accountName ? ` (${r.accountName})` : ''}` }
        : { tom: 'erro', texto: `Erro de conexão: ${r.error ?? 'desconhecido'}` };
    } catch (erro) {
      this.mensagem = {
        tom: 'erro',
        texto: `Erro de conexão: ${erro instanceof Error ? erro.message : 'desconhecido'}`,
      };
    } finally {
      this.testando = false;
      this.render();
      void this.carregar();
    }
  }

  private async desconectar(): Promise<void> {
    if (!this._api || !this._permissoes.gerenciar) return;
    if (!window.confirm('Desconectar a conta do Melhor Envio? A cotação de frete para de funcionar até reconectar.')) {
      return;
    }
    try {
      await this._api.desconectarMelhorEnvio();
      this.mensagem = { tom: 'info', texto: 'Conta desconectada.' };
    } catch {
      this.mensagem = { tom: 'erro', texto: 'não foi possível desconectar. tente novamente.' };
    }
    void this.carregar();
  }

  private render(): void {
    const gerencia = this._permissoes.gerenciar;
    const status = this.resultado.status === 'ok' ? this.resultado.dado : null;
    const estado = status?.status ?? null;
    const temToken = estado === 'connected' || estado === 'expired';

    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { margin-bottom: var(--pg-space-5, 24px); }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0 0 4px 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin: 0; }
        .cartao { max-width: 560px; }
        .linha { display: flex; justify-content: space-between; gap: var(--pg-space-4, 16px); padding: var(--pg-space-3, 12px) 0; border-bottom: 1px solid var(--pg-color-borda, #2a2f36); font-size: var(--pg-font-size-sm, 0.875rem); }
        .linha:last-of-type { border-bottom: none; }
        .linha .rotulo { color: var(--pg-color-texto-secundario, #9aa1ab); }
        .linha .valor { color: var(--pg-color-texto-primario, #f2f3f5); text-align: right; }
        .scopes { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
        .scope { font-size: var(--pg-font-size-xs, 0.75rem); padding: 2px 8px; border-radius: 9999px; background: var(--pg-color-bg-elevado, #1a1d21); border: 1px solid var(--pg-color-borda, #2a2f36); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .acoes { display: flex; flex-wrap: wrap; gap: var(--pg-space-3, 12px); margin-top: var(--pg-space-4, 16px); }
        .msg { font-size: var(--pg-font-size-sm, 0.875rem); border-radius: var(--pg-radius-md, 8px); padding: 10px 12px; margin-bottom: var(--pg-space-4, 16px); }
        .msg.sucesso { background: rgba(62,226,139,.12); color: #8af0b7; border: 1px solid rgba(62,226,139,.3); }
        .msg.erro { background: rgba(229,72,77,.12); color: #ff9d94; border: 1px solid rgba(229,72,77,.3); }
        .msg.info { background: var(--pg-color-bg-elevado, #1a1d21); color: var(--pg-color-texto-secundario, #9aa1ab); border: 1px solid var(--pg-color-borda, #2a2f36); }
        .aviso { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: var(--pg-space-3, 12px); line-height: 1.5; }
      </style>

      <div class="topo">
        <h1>Melhor Envio</h1>
        <p class="subtitulo">Integração de frete, envios e rastreamento da PG Custom.</p>
      </div>

      ${this.mensagem ? `<div class="msg ${this.mensagem.tom}">${escaparHtml(this.mensagem.texto)}</div>` : ''}

      ${this.renderCorpo(status, estado, temToken, gerencia)}
    `;

    this.root.querySelector('#btn-conectar')?.addEventListener('pg-click', () => void this.conectar());
    this.root.querySelector('#btn-reconectar')?.addEventListener('pg-click', () => void this.conectar());
    this.root.querySelector('#btn-testar')?.addEventListener('pg-click', () => void this.testar());
    this.root.querySelector('#btn-desconectar')?.addEventListener('pg-click', () => void this.desconectar());
  }

  private renderCorpo(
    status: StatusMelhorEnvio | null,
    estado: StatusMelhorEnvio['status'] | null,
    temToken: boolean,
    gerencia: boolean
  ): string {
    if (this.resultado.status === 'carregando') {
      return `<pg-card padding="confortavel"><p class="subtitulo">Carregando…</p></pg-card>`;
    }
    if (this.resultado.status === 'sem-permissao') {
      return `<pg-empty-state variant="sem-permissao" titulo="Melhor Envio" descricao="você não tem permissão para ver esta integração"></pg-empty-state>`;
    }
    if (this.resultado.status === 'indisponivel') {
      return `<pg-empty-state variant="indisponivel" titulo="Integração indisponível" descricao="${escaparHtml(this.resultado.motivo)}"></pg-empty-state>`;
    }
    if (this.resultado.status === 'erro') {
      return `<pg-empty-state variant="erro" titulo="Melhor Envio" descricao="${escaparHtml(this.resultado.mensagem)}"></pg-empty-state>`;
    }
    if (!status || !estado) {
      return `<pg-empty-state variant="vazio" titulo="Melhor Envio" descricao=""></pg-empty-state>`;
    }

    // A verificação de liveness manda no badge: se a chamada real falhou, "Conectado" vira
    // "Reautorização necessária" (âmbar), independente do que o /status otimista disse.
    const falhouLiveness = this.liveness.verificado && !this.liveness.ok;
    const rot = falhouLiveness
      ? { texto: 'Reautorização necessária', tom: 'alerta' as const }
      : ROTULO_STATUS[estado];
    const linhas: string[] = [
      `<div class="linha"><span class="rotulo">Status</span><span class="valor"><pg-badge tone="${rot.tom}">${rot.texto}</pg-badge></span></div>`,
    ];
    if (falhouLiveness && this.liveness.erro) {
      linhas.push(
        `<div class="linha"><span class="rotulo">Diagnóstico</span><span class="valor">${escaparHtml(this.liveness.erro)}</span></div>`
      );
    } else if (!this.liveness.verificado && temToken) {
      linhas.push(
        `<div class="linha"><span class="rotulo">Diagnóstico</span><span class="valor">verificando conexão…</span></div>`
      );
    }
    if (temToken) {
      linhas.push(
        `<div class="linha"><span class="rotulo">Conta</span><span class="valor">${escaparHtml(status.accountName ?? 'Não informada')}</span></div>`,
        `<div class="linha"><span class="rotulo">Autorizado em</span><span class="valor">${formatarDataHora(status.connectedAt)}</span></div>`,
        `<div class="linha"><span class="rotulo">Token expira em</span><span class="valor">${formatarDataHora(status.tokenExpiresAt)}</span></div>`,
        `<div class="linha"><span class="rotulo">Renovação automática</span><span class="valor">${status.canAutoRefresh ? 'Ativa' : 'Indisponível (reautorizar)'}</span></div>`
      );
    }
    if (status.clientId) {
      linhas.push(
        `<div class="linha"><span class="rotulo">Aplicativo (Client ID)</span><span class="valor">${escaparHtml(status.clientId)}</span></div>`
      );
    }
    if (status.scopes.length > 0) {
      linhas.push(
        `<div class="linha"><span class="rotulo">Permissões autorizadas</span><span class="valor"><span class="scopes">${status.scopes
          .map((s) => `<span class="scope">${escaparHtml(s)}</span>`)
          .join('')}</span></span></div>`
      );
    }
    linhas.push(
      `<div class="linha"><span class="rotulo">Última verificação</span><span class="valor">${formatarDataHora(status.checkedAt)}</span></div>`
    );

    const acoes: string[] = [];
    if (gerencia && estado === 'disconnected') {
      acoes.push(`<pg-button id="btn-conectar" variant="primary">Conectar Melhor Envio</pg-button>`);
    }
    if (gerencia && (estado === 'connected' || estado === 'expired')) {
      // Quando a verificação real falhou, "Reconectar" é a ação principal (primary), não a
      // secundária — é o que o operador precisa fazer para o frete voltar.
      const variante = falhouLiveness || estado === 'expired' ? 'primary' : 'secondary';
      acoes.push(`<pg-button id="btn-reconectar" variant="${variante}">Reconectar Melhor Envio</pg-button>`);
    }
    if (temToken || status.hasStaticTokenFallback) {
      acoes.push(`<pg-button id="btn-testar" variant="ghost" ${this.testando ? 'loading' : ''}>Testar conexão</pg-button>`);
    }
    if (gerencia && temToken) {
      acoes.push(`<pg-button id="btn-desconectar" variant="danger">Desconectar</pg-button>`);
    }

    const aviso =
      status.hasStaticTokenFallback && estado !== 'connected'
        ? `<p class="aviso">Um token estático de fallback está configurado no servidor — mantém a cotação funcionando, mas não renova sozinho. Conclua a autorização OAuth para ter renovação automática.</p>`
        : '';

    return `
      <pg-card padding="confortavel">
        <div class="cartao">
          ${linhas.join('')}
          <div class="acoes">${acoes.join('')}</div>
          ${aviso}
        </div>
      </pg-card>
    `;
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-melhor-envio-page', ErpMelhorEnvioPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-melhor-envio-page': ErpMelhorEnvioPage;
  }
}
